/**
 * Predictive demand-source panel — rendered inside the Scaling tab's
 * Demand Sources → Predictive sub-tab when the source is enabled.
 *
 * Forecast configuration (shadow toggle, lookahead, quantile, timezone,
 * seed-from-history) alongside the IL-137 observability surfaces: the large
 * unit-labeled forecast-vs-actual chart, the bin-fill heatmap, and the
 * quarantine list. Enable/disable lives on the sub-tab's toggle row, owned
 * by ScalingTab.
 */

import { useState } from 'react';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import { PredictiveObservability } from '../PredictiveObservability';
import type { BinRow, PredictionRow, QuarantineRow } from './shared';
import type { PoolConfigDraftApi } from './usePoolConfigDraft';

export interface PredictiveSourcePanelProps {
  draftApi: PoolConfigDraftApi;
  saving: boolean;
  predictions: PredictionRow[];
  histogramBins: BinRow[];
  quarantine: QuarantineRow[];
  /** Timezone the bins endpoint actually served (authoritative for display). */
  predictiveTimezoneServed: string;
  onSeedHistory: () => Promise<string>;
}

export function PredictiveSourcePanel({
  draftApi,
  saving,
  predictions,
  histogramBins,
  quarantine,
  predictiveTimezoneServed,
  onSeedHistory,
}: PredictiveSourcePanelProps) {
  const { draft, patch } = draftApi;
  const [seedBusy, setSeedBusy] = useState(false);
  const [seedStatus, setSeedStatus] = useState<string | null>(null);

  if (!draft) return null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
      <div className={styles.formHelp} style={{ marginBottom: 0 }}>
        Forecasts demand from a weekly adaptive histogram of past observations. Returns the
        smoothed quantile at <code>now + lookahead</code> as a floor — competes with
        reactive/scheduled via max(). Cold-starts as floor=0 until ≥ 4 weeks of history accrue.
      </div>

      <label style={{ display: 'flex', gap: 'var(--space-1)', alignItems: 'flex-start', fontSize: 'var(--text-xs)' }}>
        <input
          type="checkbox"
          checked={draft.predictiveShadowMode}
          onChange={e => patch({ predictiveShadowMode: e.target.checked })}
          disabled={saving}
        />
        <span>
          <strong>Shadow mode</strong> — run the pipeline and log forecasts to <code>pool_demand_predictions</code>,
          but contribute floor=0 to mediation. Use for ≥ 1 week of validation before flipping to active.
        </span>
      </label>

      <div className={styles.fieldCluster}>
        <div>
          <label className={styles.formLabel} htmlFor="pred-lookahead">Lookahead (minutes)</label>
          <input
            id="pred-lookahead"
            className={styles.formInputNarrow}
            type="number" min="1"
            value={draft.lookaheadMinutes}
            onChange={e => patch({ lookaheadMinutes: e.target.value })}
            disabled={saving}
            title="Forecast horizon — match the backend's warm-up time so capacity lands when the demand does."
          />
        </div>
        <div>
          <label className={styles.formLabel} htmlFor="pred-quantile">Forecast Quantile</label>
          <input
            id="pred-quantile"
            className={styles.formInputNarrow}
            type="number" min="0.5" max="0.99" step="0.05"
            value={draft.predictiveQuantile}
            onChange={e => patch({ predictiveQuantile: e.target.value })}
            disabled={saving}
            title="Which quantile of the per-bin distribution to forecast (0.5–0.99). Higher = more headroom, more cost."
          />
        </div>
        <div>
          <label className={styles.formLabel} htmlFor="pred-tz">Timezone (IANA)</label>
          <input
            id="pred-tz"
            className={styles.formInputWide}
            type="text"
            placeholder="UTC"
            value={draft.predictiveTimezone}
            onChange={e => patch({ predictiveTimezone: e.target.value })}
            disabled={saving}
            title="Bin boundaries follow this timezone — set it to the customer's business timezone so weekly patterns line up."
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <button
          type="button"
          className={styles.actionButton}
          disabled={seedBusy}
          onClick={async () => {
            setSeedBusy(true);
            setSeedStatus('Seeding from session history…');
            try {
              setSeedStatus(await onSeedHistory());
            } catch (err) {
              setSeedStatus(`Seed failed: ${err instanceof Error ? err.message : String(err)}`);
            } finally {
              setSeedBusy(false);
            }
          }}
        >
          Seed from history (6w)
        </button>
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--text-secondary)' }}>
          {seedStatus ?? 'Pre-populates the forecast histogram from the last 6 weeks of session_usage so predictive passes its 4-week gate immediately. Replaces existing histogram data.'}
        </span>
      </div>

      {/* Observability — large, unit-labeled charts */}
      <PredictiveObservability
        predictions={predictions}
        bins={histogramBins}
        quarantine={quarantine}
        tz={predictiveTimezoneServed}
      />
    </div>
  );
}
